/*
 * File: abbyCadabbyApi.ts
 * Project: rosita
 * -----
 * Last Modified: 03 Mar 2022 12:22:31 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { ApiMultipleResponse } from 'models/ApiModels';
import { Application, ApplicationParams, ApplicationPayload } from 'models/Application';
import { ReduxState } from 'redux/store';

const baseUrl = process.env.REACT_APP_FREDDY_CADABBY_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_FREDDY_CADABBY;

export const freddyCadabbyApi = createApi({
  reducerPath: 'freddyCadabbyApi',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState, endpoint }) => {
      const token = (getState() as ReduxState).app.accessToken;

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        headers.set('x-functions-key', functionsKey);
      }

      if (endpoint !== 'updateApplicationLogo') headers.set('Content-Type', 'application/json');

      return headers;
    }
  }),
  tagTypes: ['Application', 'Applications'],
  endpoints: (builder) => ({
    getApplication: builder.query<Application, string>({
      query: (applicationId) => ({
        url: `/applications/${applicationId}`
      }),
      providesTags: ['Application']
    }),
    updateApplication: builder.mutation<Application, { applicationId: string; payload: ApplicationPayload }>({
      query: ({ applicationId, payload }) => ({
        url: `/applications/${applicationId}`,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: ['Application', 'Applications']
    }),
    updateApplicationLogo: builder.mutation<Application, { applicationId: string; payload: FormData }>({
      query: ({ applicationId, payload }) => ({
        url: `/applications/${applicationId}/logo`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['Applications', 'Application']
    }),
    deleteApplication: builder.mutation<Application, { applicationId: string }>({
      query: ({ applicationId }) => ({
        url: `/applications/${applicationId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Application', 'Applications']
    }),
    recoverApplication: builder.mutation<Application, { applicationId: string }>({
      query: ({ applicationId }) => ({
        url: `/applications/${applicationId}/recover`
      }),
      invalidatesTags: ['Application', 'Applications']
    }),
    createApplication: builder.mutation<Application, { payload: ApplicationPayload }>({
      query: ({ payload }) => ({
        url: '/applications',
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['Application']
    }),
    getApplications: builder.query<ApiMultipleResponse<Application>, ApplicationParams>({
      query: (params) => ({
        url: '/applications/',
        params
      }),
      providesTags: ['Applications']
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const {
  useGetApplicationQuery,
  useGetVersionQuery,
  useGetApiNameQuery,
  useCreateApplicationMutation,
  useGetApplicationsQuery,
  useUpdateApplicationMutation,
  useDeleteApplicationMutation,
  useRecoverApplicationMutation,
  useUpdateApplicationLogoMutation
} = freddyCadabbyApi;
